import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { isSafari, isFirefox, isEdge } from "react-device-detect";
import { isWebGLAvailable } from "../libs/webglSupport";
import { copyTextToClipboard } from "../utils/copyTextToClipboard";

/**
 * Browsers block navigation to about:/chrome:// from web content, so a link
 * here would render clickable and do nothing. Copy-to-paste is the only
 * working affordance.
 */
const CopyableUrl = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!(await copyTextToClipboard(url))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Tooltip title={copied ? "Copied" : "Click to copy"}>
      <Box
        component="button"
        type="button"
        onClick={handleCopy}
        aria-label={`Copy ${url}`}
        sx={{
          font: "inherit",
          fontFamily: "monospace",
          color: "inherit",
          background: "none",
          border: 0,
          p: 0,
          cursor: "pointer",
          textDecoration: "underline dotted",
          textUnderlineOffset: 2,
        }}
      >
        {url}
      </Box>
    </Tooltip>
  );
};

// Renders about:* / chrome://* tokens in help copy as click-to-copy.
const withCopyableUrls = (text: string) =>
  text
    .split(/(about:[a-z]+|chrome:\/\/[a-z]+)/g)
    .map((part, i) =>
      part.startsWith("about:") || part.startsWith("chrome://") ? (
        <CopyableUrl key={i} url={part} />
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      ),
    );

/**
 * The browser reports "GPU disabled" identically whether WebGL is blocked, the
 * driver is blocklisted, or the GPU process crashed — so suggest fixes rather
 * than assert a cause, and send the user to the page that states the real one.
 *
 * Chrome's acceleration toggle is last on purpose: switching it OFF still
 * renders fine (software fallback), so it is rarely the cause. The gpu page is
 * pointed at as something to send support, not to self-diagnose: its WebGL row
 * reads "Software only" on healthy machines too. Edge needs its own branch —
 * chrome:// is not reachable there, and it says InPrivate. Safari has no such
 * setting at all; Lockdown Mode is its usual cause.
 * ~90% of MATTOBOARD-3V4 is Chromium, 8% Safari, 1% Firefox.
 */
const webglHelp = (): {
  fixes: string[];
  diagnostic: { url: string; where: string } | null;
} => {
  if (isSafari)
    return {
      fixes: [
        "Check that Lockdown Mode is off for this site, under Settings → Privacy & Security",
        "Restart Safari",
      ],
      diagnostic: null,
    };
  if (isFirefox)
    return {
      fixes: [
        "Restart Firefox",
        "Check that webgl.disabled is false in about:config — privacy extensions often turn it on",
        'Search your Firefox settings for "hardware acceleration" and turn it on',
      ],
      diagnostic: {
        url: "about:support",
        where: "and include what it says if you contact us — it names the exact cause",
      },
    };
  if (isEdge)
    return {
      fixes: [
        "Restart your browser — 3D can stop starting after a graphics crash",
        "Try an InPrivate window — some privacy extensions block 3D",
        "On a work laptop, ask IT whether 3D graphics are blocked by policy",
        'Failing that, check Settings → System and performance → "Use graphics acceleration when available" is on',
      ],
      diagnostic: {
        url: "edge://gpu",
        where: "and include what it says if you contact us — it names the exact cause",
      },
    };
  return {
    fixes: [
      "Restart your browser — 3D can stop starting after a graphics crash",
      "Try an Incognito window — some privacy extensions block 3D",
      "On a work laptop, ask IT whether 3D graphics are blocked by policy",
      'Failing that, check Settings → System → "Use graphics acceleration when available" is on',
    ],
    diagnostic: {
      url: "chrome://gpu",
      where: "and include what it says if you contact us — it names the exact cause",
    },
  };
};

export type BoardLoadStatus = "retrying" | "failed";
/** Only a connectivity failure is worth retrying; access and GPU failures never are. */
export type BoardLoadErrorVariant = "network" | "permission" | "webgl";

interface BoardLoadErrorProps {
  status: BoardLoadStatus;
  variant?: BoardLoadErrorVariant;
  /** Only the network variant retries. */
  onRetry?: () => void;
}

/**
 * Overlay for a failed board-document read (issue #3808). Rendered by LoadScene,
 * which is mounted by both /board/:id and /show/:id; fixed so it does not depend on
 * where in either tree LoadScene sits.
 */
export const BoardLoadError = ({
  status,
  variant = "network",
  onRetry,
}: BoardLoadErrorProps) => (
  <Stack
    // Only the terminal failure should interrupt a screen reader.
    role={status === "retrying" ? "status" : "alert"}
    alignItems="center"
    justifyContent="center"
    spacing={2}
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 1400,
      backgroundColor: "background.default",
      textAlign: "center",
      p: 3,
    }}
  >
    {status === "retrying" && (
      <>
        <CircularProgress size={28} />
        <Typography variant="body1">Still loading this board…</Typography>
        <Typography variant="body2" color="text.secondary">
          Your connection looks slow. Retrying.
        </Typography>
      </>
    )}

    {status === "failed" && variant === "permission" && (
      <>
        <Typography variant="h6">
          You don&apos;t have access to this board
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          Ask the owner to share it with you, or sign in with an account that
          has access.
        </Typography>
        {/* No Retry — re-reading returns the same answer, and this overlay covers
            the nav bar, so offer the one action that leads somewhere. */}
        <Button variant="contained" href="/home/boards">
          Go to my boards
        </Button>
      </>
    )}

    {status === "failed" && variant === "webgl" && (
      <>
        <Typography variant="h6">
          3D isn&apos;t available in this browser
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          Mattoboard needs your browser&apos;s 3D graphics, and they aren&apos;t
          starting on this device. Worth trying:
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="ul"
          sx={{ maxWidth: 420, textAlign: "left", m: 0, pl: 2.5 }}
        >
          {webglHelp().fixes.map((fix) => (
            <li key={fix}>{withCopyableUrls(fix)}</li>
          ))}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          {webglHelp().diagnostic && (
            <>
              Open <CopyableUrl url={webglHelp().diagnostic!.url} /> in a new
              tab {webglHelp().diagnostic?.where}.{" "}
            </>
          )}
          On a work-managed device it may be blocked by IT policy.
        </Typography>
        {/* No Reload — a reload can never succeed while the GPU is unavailable. */}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" href="/home/boards">
            Go to my boards
          </Button>
          <Button variant="outlined" href="mailto:support@mattoboard.com">
            Contact support
          </Button>
        </Stack>
      </>
    )}

    {/* Explicit, not a fallthrough: a new variant must never inherit Retry. */}
    {status === "failed" && variant === "network" && (
      <>
        <Typography variant="h6">Couldn&apos;t load this board</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          We couldn&apos;t reach Mattoboard to fetch this board. Check your
          connection and try again.
        </Typography>
        <Button variant="contained" onClick={onRetry}>
          Retry
        </Button>
      </>
    )}
  </Stack>
);

let webglReported = false;

/**
 * Shown when the client can't create a WebGL context (MATTOBOARD-3V4). Reported
 * once per session under its own contextName so it stays out of that issue.
 */
export const WebGLUnavailable = () => {
  useEffect(() => {
    if (webglReported) return;
    webglReported = true;
    import("../libs/sentry")
      .then(({ sendExceptionToSentry }) =>
        sendExceptionToSentry({
          error: new Error("WebGL unavailable — board editor gated"),
          contextName: "webgl_unavailable",
          context: { userAgent: navigator?.userAgent ?? null },
        }),
      )
      .catch(() => {});
  }, []);

  return <BoardLoadError status="failed" variant="webgl" />;
};

/**
 * Route-level gate. Must wrap the route element, not the Canvas: the editor
 * chrome (LoadScene, sidebar, ActionBar, AutoCompose) lives outside BoardLazy
 * and would otherwise keep fetching and firing behind the overlay.
 */
export const RequireWebGL = ({ children }: { children: React.ReactNode }) =>
  isWebGLAvailable() ? <>{children}</> : <WebGLUnavailable />;
